import type { IDataSource, IPaginatedResult } from "@/data/dao/service/BaseServiceDao";
import { Format } from "@/data/util/Format";

import { logError } from "@/shared/logging/errors";

export interface IOptionNameFormatter<T> {
    (e: T): string;
}

export interface ISortBy<T> {
    (a: T[]): T[];
}

export class Select<T> {
    private readonly noneOptionValue = "none";
    
    private $select: JQuery;
    private dataSource?: IDataSource<T>;
    private nameFormatter?: IOptionNameFormatter<T>;
    private internalValue?: string | Array<string> = undefined;
    private showAllText?: string;
    private keyProperty?: string;
    private isMultiSelect = false;
    private orderBy?: string;
    private sortBy?: ISortBy<T>;
    private entries?: IPaginatedResult<T>;

    public onChange?: { (value: string | undefined) };
    private isInitialized = false;
    private suppressEvents = false;

    constructor(
        $select?: JQuery,
        dataSource?: IDataSource<T>,
        formatter?: IOptionNameFormatter<T>,
        lazyInit?: boolean,
        showAllText?: string,
        keyProp?: string,
        multiSelect?: boolean,
        orderBy?: string,
        sortBy?: ISortBy<T>
    ) {
        if (multiSelect !== undefined) this.isMultiSelect = multiSelect;

        this.$select = $select === undefined ? $('<select class="form-control kt-selectpicker" />') : $select;
        if (this.isMultiSelect === true) this.$select.attr("multiple", "true");

        this.keyProperty = keyProp;
        this.showAllText = showAllText;
        this.dataSource = dataSource;
        this.nameFormatter = formatter;
        this.orderBy = orderBy;
        this.sortBy = sortBy;

        if ($select !== undefined) {
            this.$select.selectpicker();
            this.isInitialized = true;
        }

        this.$select.on("changed.bs.select", (_e, index) => {
            if (this.suppressEvents === true) return;

            let newVal = this.$select.selectpicker("val");
            if (this.isMultiSelect) {
                if (this.showAllText !== undefined) {
                    this.suppressEvents = true;
                    if (newVal.length === 0) {
                        this.$select.selectpicker("val", ["none"]);
                    } else {
                        if (index === 0) this.$select.selectpicker("val", ["none"]);
                        else
                            this.$select.selectpicker(
                                "val",
                                newVal.filter((c) => c !== "none")
                            );
                    }

                    this.suppressEvents = false;
                }

                this.internalValue = newVal;
                if (this.onChange !== undefined) this.onChange(this.getValue());
            } 
            else {
                if (newVal == null) return;

                if (newVal === "none") newVal = undefined;

                if (this.internalValue !== newVal) {
                    this.internalValue = newVal;
                    if (this.onChange !== undefined) this.onChange(this.getValue());
                }
            }
        });

        if (this.dataSource !== undefined && (lazyInit === undefined || lazyInit === false)) {
            window.setTimeout(() => {
                this.fetchData();
                this.$select.selectpicker("refresh");
            }, 1);
        } else {
            if (this.showAllText !== undefined) {
                if (this.$select.find('option[value="none"]').length === 0) {
                    this.$select.prepend('<option value="none">' + this.showAllText + "</option>");
                    this.$select.selectpicker("refresh");
                    const currentlySelectedVal = this.$select.find('option[selected="selected"]');
                    if (currentlySelectedVal.length === 0) this.$select.selectpicker("val", "none");
                    else this.internalValue = this.$select.selectpicker("val");

                    return;
                }
            }

            this.$select.selectpicker("refresh");
            this.internalValue = this.$select.selectpicker("val");
        }
    }

    public getValue(): string | undefined {
        if (this.isMultiSelect) return this.getValues() as any;

        let value = this.$select.selectpicker("val");
        if (value === "none") return undefined;

        if (this.internalValue === undefined) return this.internalValue;

        if (value == null && this.internalValue !== undefined) value = this.internalValue;

        return value == null ? undefined : value;
    }

    public getValues(): Array<string> {
        const vals = this.$select.selectpicker("val");

        if (this.isMultiSelect) {
            if (vals.length === 1 && vals.includes("none")) return [];
        }

        return vals;
    }

    public getOptions(): Array<string> {
        const options: string[] = [];
        this.$select.find("option").each((i, ele) => {
            options.push($(ele).val());
        });

        return options;
    }

    public setValue(value?: string | number, triggerOnChange: boolean = false): Select<T> {
        this.suppressEvents = true;

        this.internalValue = value as any;
        if (value === undefined && this.showAllText !== undefined)
        {
            value = "none";
        }

        this.$select.selectpicker("val", value == undefined ? null : value);
        this.suppressEvents = false;

        if (triggerOnChange && this.onChange != null && value != null) {
            this.onChange(value.toString());
        }

        return this;
    }

    public setValues(values: Array<string | number> | undefined): Select<T> {
        this.suppressEvents = true;

        const newValues = values == undefined ? [] : values;
        
        if (this.showAllText !== undefined && newValues.length === 0)
        {
            this.$select.selectpicker("val", ["none"]);
        }
        else
        {
            this.$select.selectpicker("val", values == undefined ? [] : values);
        }

        this.internalValue = values as Array<string>;

        this.suppressEvents = false;
        return this;
    }

    public getModel(): T | undefined {
        const v = this.getValue();
        if (v === undefined) return undefined;

        return this.$select.find('> option[value="' + v + '"]').data("model");
    }

    public setError(hasError: boolean): Select<T> {
        if (hasError) this.$select.addClass("is-invalid");
        else this.$select.removeClass("is-invalid");

        this.$select.selectpicker("setStyle");
        return this;
    }

    public setDisabled(isDisabled: boolean): Select<T> {
        this.$select.prop("disabled", isDisabled);
        this.$select.selectpicker("refresh");
        return this;
    }

    public getEntries(): Array<T> {
        return this.entries?.items ?? [];
    }
    
    public async fetchData(finished?: { () }, abortSignal?: AbortSignal): Promise<void> {
        try {
            const entries = await this.dataSource!.getData({ limit: 10000, orderBy: this.orderBy }, abortSignal);
            this.entries = entries;

            this.$select.empty();

            if (this.showAllText !== undefined) {
                this.$select.append('<option value="' + this.noneOptionValue + '">' + this.showAllText + "</option>");
            }

            const items = this.sortBy !== undefined ? this.sortBy(entries.items) : entries.items;
            for (const entry of items) {
                let name = this.nameFormatter !== undefined ? this.nameFormatter(entry) : undefined;
                if (name === undefined && entry["name"] !== undefined) name = entry["name"];

                if (name === undefined) name = "unresolved name";

                const key = entry[this.keyProperty === undefined ? "id" : this.keyProperty];
                $(`<option value="${key}">${Format.htmlEscape(name)}</option>`)
                    .data("model", entry)
                    .appendTo(this.$select);
            }

            this.$select.selectpicker("refresh");
            if (this.internalValue !== undefined && this.internalValue != null) {
                this.$select.selectpicker("val", this.internalValue);
            } else if (this.showAllText !== undefined)
                this.$select.selectpicker("val", this.isMultiSelect ? [] : this.noneOptionValue);
            else if (entries.items.length > 0) {
                this.$select.selectpicker(
                    "val",
                    this.isMultiSelect ? [] : entries.items[0][this.keyProperty === undefined ? "id" : this.keyProperty]
                );
                this.internalValue = this.isMultiSelect
                    ? []
                    : entries.items[0][this.keyProperty === undefined ? "id" : this.keyProperty];
            }

            this.$select.selectpicker("refresh");
        } catch (ex) {
            logError(ex);
            this.$select.empty();
            if (this.showAllText !== undefined) {
                this.$select.append(`<option value="${this.noneOptionValue}">${this.showAllText}</option>`);
            }
        } finally {
            if (finished !== undefined) finished();
        }
    }

    public renderTo($element: JQuery): Select<T> {
        this.$select.appendTo($element);

        if (!this.isInitialized) {
            this.$select.selectpicker();
            this.isInitialized = true;
        }

        return this;
    }

    public reset(): Select<T> {
        if (this.showAllText !== undefined) this.setValue(undefined);
        else {
            //get first select
            this.setValue(this.$select.find("> option").first().attr("value"));
        }

        return this;
    }

    public destroy() {
        this.$select.selectpicker("destroy");
        this.$select.remove();
    }

    public static createFromSelect<T>(
        $select: JQuery,
        dataSource?: IDataSource<T>,
        formatter?: IOptionNameFormatter<T>,
        lazyInit?: boolean,
        showAllText?: string,
        keyProp?: string,
        multiSelect?: boolean,
        orderBy?: string,
        sortBy?: ISortBy<T>
    ): Select<T> {
        return new Select($select, dataSource, formatter, lazyInit, showAllText, keyProp, multiSelect, orderBy, sortBy);
    }

    public static createFromSelectWithOptions<T>(createFromSelectOptions: CreateFromSelectOptions<T>): Select<T> {
        return new Select(
            createFromSelectOptions.$select,
            createFromSelectOptions.dataSource,
            createFromSelectOptions.formatter,
            createFromSelectOptions.lazyInit,
            createFromSelectOptions.showAllText,
            createFromSelectOptions.keyProp,
            createFromSelectOptions.multiSelect,
            createFromSelectOptions.orderBy,
            createFromSelectOptions.sortBy
        );
    }
}

export type CreateFromSelectOptions<T> = {
    $select: JQuery;
    dataSource?: IDataSource<T>;
    formatter?: IOptionNameFormatter<T>;
    lazyInit?: boolean;
    showAllText?: string;
    keyProp?: string;
    multiSelect?: boolean;
    orderBy?: string;
    sortBy?: ISortBy<T>;
};
